import {Component, OnInit} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {RxComponent} from 'app/core/components/common/rx.component';
import {ModalSize, SmartModalService} from '../../../../../core/services/smart-modal.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ValidityPeriodFormComponent} from './validity-period-form/validity-period-form.component';
import {CertificatesService} from '../../certificates.service';
import {Certificate} from '../../models';
import {TooltipConfig} from '../../../../../shared/components/smart-tooltip/smart-tooltip.component';
import {DeactivateCertificateTemplateComponent} from './deactivate-certificate-template/deactivate-certificate-template.component';
import {CertificateNameFormComponent} from './certificate-name-form/certificate-name-form.component';
import {DeactivateCertificateConfirmComponent} from './deactivate-certificate-confirm/deactivate-certificate-confirm.component';
import {ConfigService} from '../../../../../core/services/config.service';
import {TemplateFileFormComponent} from './template-file-form/template-file-form.component';

declare var moment;

@Component({
    selector: 'itpx-certificate-details',
    templateUrl: './certificate-details.component.html',
    styleUrls: ['./certificate-details.component.css']
})
export class CertificateDetailsComponent extends RxComponent implements OnInit {
    public readonly tooltopConfig: TooltipConfig = {
        icon: 'fa-info-circle',
        fontAwesomeStyle: 'fa',
        hoverable: true,
        hoverStyle: 'background',
        placement: 'bottom',
        enableHtml: true
    }

    certificateDetails: Certificate;
    certificateId: string;
    private form: FormGroup;
    private certificateToReplace: Certificate;


    isSellerAdmin: boolean;
    public isActivated: boolean;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private modalService: SmartModalService,
                private fb: FormBuilder,
                private service: CertificatesService,
                private configService: ConfigService) {
        super();
    }

    ngOnInit() {
        this.route.params.take(1).switchMap(params => {
            this.certificateId = params['id'];
            return this.service.getCertificateDetails(this.certificateId)
        })
            .bindToLifecycle(this)
            .subscribe(res => {
                if (!res) {
                    return;
                }
                this.certificateDetails = res;
                this.form = this.fb.group({
                    name: [this.certificateDetails.displayName, [Validators.required, Validators.minLength(5), Validators.maxLength(60)]],
                    validityPeriod: [this.certificateDetails.validityPeriod || 1],
                    agree: [!this.certificateDetails.validityPeriod],
                    fileName: [this.certificateDetails.fileName],
                    bucket: [this.certificateDetails.s3Bucket],
                    key: [this.certificateDetails.s3Key]
                });
                this.isActivated = this.certificateDetails.status && this.certificateDetails.status.configKey.toLowerCase() === 'active';
            });
    }

    ngOnDestroy() {
    }

    formatDate(dt: string) {
        if (dt)
            return moment(dt).format('DD-MM-YYYY, [at] HH:mm:ss')
    }

    public onChangeActivateToggle(event: any): void {
        if (this.isActivated) {
            this.service.toggleStatusCertificate(this.certificateDetails)
                .bindToLifecycle(this)
                .toPromise()
                .then(res => {
                    this.certificateDetails.status = res.status;
                })
            return;
        }
        this.isActivated = true;
        this.certificateToReplace = null;
        this.modalService.open(DeactivateCertificateTemplateComponent, this.certificateDetails,
            'Deactivate Certificate Template?', ModalSize.Md, 'center', true
        )
            .take(1)
            .filter(data => data && data.result)
            .switchMap(data => {
                let newId: string = '';
                if (data.result.selectedCertificate) {
                    this.certificateToReplace = data.result.selectedCertificate;
                    newId = this.certificateToReplace.id;
                }
                return this.service.toggleStatusCertificate(this.certificateDetails, newId);
            })
            .neverError()
            .subscribe((data: any) => {
                this.isActivated = false;
                if (this.certificateToReplace) {
                    setTimeout(() => {
                        this.openCertificateDeactivateConfirmModal();
                    }, 400)
                }
            });
    }

    public onEditPropClicked(property: string) {
        switch (property) {
            case 'name':
                this.openNameEditModal()
                break;
            case 'validityPeriod':
                this.openValidityEditModal()
                break;
            case 'templateFile':
                this.openTemplateFileEditModal()
                break;
            default:
                throw new Error(`Unknown option ${property}`);
        }
    }

    private openCertificateDeactivateConfirmModal() {
        if (!this.certificateDetails || !this.certificateToReplace) {
            return;
        }
        this.modalService.open(
            DeactivateCertificateConfirmComponent,{
                certificate: this.certificateDetails,
                certificateToReplace: this.certificateToReplace
            },
            'Certificate Template Replaced',
            ModalSize.Md,
            'center',
            true
        )
            .take(1)
            .filter(data => data && data.result)
            .bindToLifecycle(this)
            .subscribe((data: any) => {
                window.open(`/organization/certificates/${this.certificateToReplace.id}/details`, '_blank', 'noopener,noreferrer');
            }, error => {
            });
    }

    onTabClick(e) {
        let tabId = e.srcElement.attributes['href'].value.substr(1);
        this.router.navigate(['/organization/certificates', this.certificateId, 'details'], {queryParams: {tab: tabId}});
    }

    private openNameEditModal() {
        this.form.patchValue({ name: this.certificateDetails.displayName });
        this.modalService.open(CertificateNameFormComponent, { form: this.form },
            'Edit Certificate Template Name', ModalSize.Md, 'center', true,
            'Please be aware that any changes to this information will be propagated to all future courses or ' +
            'exams issuing this certificate. No changes will be done to already released certificates'
        )
            .take(1)
            .filter(data => data && data.result)
            .switchMap(data => {
                const reqPayload: Certificate = { ...this.certificateDetails, displayName: data.result };
                return this.service.editCertificate(reqPayload);
            })
            .neverError()
            .bindToLifecycle(this)
            .subscribe((data: Certificate) => this.certificateDetails.displayName = data.displayName);
    }

    private openValidityEditModal() {
        this.form.patchValue({ validityPeriod: this.certificateDetails.validityPeriod });
        this.modalService.open(ValidityPeriodFormComponent, { form: this.form },
            'Edit Certificate Template Type', ModalSize.Md, 'center', true,
            'Please be aware that any changes to this information will be propagated to all future courses or ' +
            'exams issuing this certificate. No changes will be done to already released certificates'
        )
            .take(1)
            .filter(data => data && data.result)
            .switchMap(data => {
                const reqPayload: Certificate = { ...this.certificateDetails, validityPeriod: data.result.validityPeriod };
                return this.service.editCertificate(reqPayload);
            })
            .neverError()
            .bindToLifecycle(this)
            .subscribe((data: Certificate) => {
                this.certificateDetails.validityPeriod = data.validityPeriod;
            });
    }

    private openTemplateFileEditModal() {
        this.form.patchValue({
            fileName: this.certificateDetails.fileName,
            key: this.certificateDetails.s3Key,
            bucket: this.certificateDetails.s3Bucket
        })
        this.modalService.open(TemplateFileFormComponent, { form: this.form },
            'Edit Certificate Template File', ModalSize.Md, 'center', true,
            'Please be aware that any changes to this information will be propagated to all future courses or ' +
            'exams issuing this certificate. No changes will be done to already released certificates'
        )
            .take(1)
            .filter(data => data && data.result)
            .switchMap(data => {
                const reqPayload: Certificate = {
                    ...this.certificateDetails,
                    fileName: data.result.fileName,
                    s3Bucket: data.result.bucket,
                    s3Key: data.result.key
                };
                return this.service.editCertificate(reqPayload);
            })
            .neverError()
            .bindToLifecycle(this)
            .subscribe((data: Certificate) => {
                this.certificateDetails.fileName = data.fileName;
                this.certificateDetails.s3Bucket = data.s3Bucket;
                this.certificateDetails.s3Key = data.s3Key;
            });
    }

    public get dowloadTemplateLink(): string {
        const link = `${this.configService.getSphinxUrlUnsecured('sahara', this.certificateDetails.s3Bucket, this.certificateDetails.s3Key)}`;
        return link;
    }
}




// WEBPACK FOOTER //
// ./src/app/organization/components/certificates/components/details/certificate-details.component.ts